import React, { useEffect, useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { Wallet, Banknote, CreditCard, Smartphone, MapPin, Phone, ShoppingBag, Gift, ShieldCheck, FlaskConical, CheckCircle2, XCircle, Clock, Globe2, Loader2, LocateFixed } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { useTranslation } from "react-i18next";
import { apiClient, formatApiError } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { formatGNF, PAYMENT_LABEL } from "@/utils/format";
import { fmtReopen } from "@/utils/reopen";
import { toast } from "sonner";
import { normalisePhoneGuinea, normalisePhoneInternational, isValidPhoneGuinea, isValidPhoneInternational } from "@/utils/phone";
import { useNotifications } from "@/contexts/NotificationsContext";
import { roleHome } from "@/utils/roles";
import { useDeliveryAddress } from "@/contexts/AddressContext";
import { isNativeApp } from "@/utils/platform";
import ClientPositionMap from "@/components/ClientPositionMap";
import CheckoutSuggestions from "@/components/CheckoutSuggestions";

// Sandbox MTN MoMo msisdn cheat-sheet — admin-only.
// Values mirror momo_service.SANDBOX_TEST_NUMBERS (Uganda +256 sandbox format).
const MOMO_TEST_MSISDN = [
  { key: "success", label: "Succès", msisdn: "+22546733123453", icon: CheckCircle2, color: "#009E60" },
  { key: "pending", label: "Rejeté (pending)", msisdn: "+22546733123451", icon: Clock, color: "#F4C430" },
  { key: "failed", label: "Expiré", msisdn: "+22546733123452", icon: XCircle, color: "#E32636" },
];

const PAYMENTS = [
  { key: "cash", labelKey: "checkout.cash", icon: Wallet, color: "#1A1A1A" },
  { key: "orange_money", labelKey: "checkout.orange", icon: Smartphone, color: "#FF7900" },
  { key: "mtn_money", labelKey: "checkout.mtn", icon: Smartphone, color: "#FFCC00" },
  { key: "card", labelKey: "checkout.card", icon: CreditCard, color: "#1A73E8" },
  { key: "paypal", labelKey: "co.paypalLabel", icon: Globe2, color: "#003087" },
];

export default function Checkout() {
  const { t, i18n } = useTranslation();
  const { cart, subtotal, count, clear, addItem } = useCart();
  const { user, loading: authLoading } = useAuth();
  const { selected: appAddr } = useDeliveryAddress();
  const { refresh: refreshNotifications } = useNotifications();
  const navigate = useNavigate();
  const [zones, setZones] = useState([]);
  const [form, setForm] = useState({
    address: "",
    zone: "",
    phone: user?.phone || "",
    payment_method: "cash",
    notes: "",
    momo_number: "",
  });
  const [loading, setLoading] = useState(false);
  const [usePoints, setUsePoints] = useState(0);
  const [fromDiaspora] = useState(() => {
    // Arrived from the « Offrir un repas » (Diaspora) landing page
    try { return localStorage.getItem("neldaari_gift_intent") === "1"; } catch (e) { void e; return false; }
  });
  const [isGift, setIsGift] = useState(fromDiaspora);
  useEffect(() => {
    try { localStorage.removeItem("neldaari_gift_intent"); } catch (e) { void e; }
  }, []);
  const [gift, setGift] = useState({ recipient_name: "", recipient_phone: "", message: "", recipient_address: "", recipient_landmark: "", payer_country: "", zone: "" });
  const [omWait, setOmWait] = useState(null); // { order_id, pay_token, simulated } while polling OM
  const [gps, setGps] = useState(null); // { lat, lng, accuracy } shared by the client for the driver
  const [gpsStatus, setGpsStatus] = useState("idle"); // idle | loading | ok | error
  const [coverage, setCoverage] = useState(null); // { has_zones, covered, zone } — zones de livraison (polygones admin)
  const [savedAddresses, setSavedAddresses] = useState([]); // address book (Maison, Bureau…)
  const [selectedAddrId, setSelectedAddrId] = useState(null);
  const [saveLabel, setSaveLabel] = useState("");
  const [savingAddr, setSavingAddr] = useState(false);
  const [savedRecipients, setSavedRecipients] = useState([]); // carnet de proches (cadeaux diaspora)
  const [selectedRecipientId, setSelectedRecipientId] = useState(null);
  const [savingRecipient, setSavingRecipient] = useState(false);

  const applyRecipient = (r) => {
    setGift((g) => ({
      ...g,
      recipient_name: r.name,
      recipient_phone: r.phone,
      recipient_address: r.address,
      recipient_landmark: r.landmark || "",
      zone: r.zone,
    }));
    setSelectedRecipientId(r.recipient_id);
  };

  const saveCurrentRecipient = async () => {
    if (!gift.recipient_name.trim() || !gift.recipient_phone.trim() || !gift.recipient_address.trim() || !gift.zone) {
      toast.error(t("co.errGiftSave"));
      return;
    }
    setSavingRecipient(true);
    try {
      const { data } = await apiClient.post("/me/recipients", {
        name: gift.recipient_name.trim(),
        phone: gift.recipient_phone.trim(),
        address: gift.recipient_address.trim(),
        zone: gift.zone,
        landmark: gift.recipient_landmark.trim(),
      });
      setSavedRecipients((list) => {
        const others = list.filter((r) => r.recipient_id !== data.recipient_id);
        return [...others, data];
      });
      setSelectedRecipientId(data.recipient_id);
      toast.success(`« ${data.name} » enregistré dans votre carnet de proches`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingRecipient(false);
    }
  };

  const applyAddress = (a) => {
    setForm((f) => ({ ...f, address: a.address, zone: a.zone }));
    setSelectedAddrId(a.address_id);
    if (a.gps?.lat != null) {
      setGps({ lat: a.gps.lat, lng: a.gps.lng, accuracy: a.gps.accuracy ?? null, saved: true });
      setGpsStatus("ok");
    } else {
      setGps(null);
      setGpsStatus("idle");
    }
  };

  // App mobile : l'adresse choisie dans le header de l'accueil suit le client
  // jusqu'au checkout — pré-sélection en un geste (une seule fois, modifiable).
  const appAddrApplied = React.useRef(false);
  useEffect(() => {
    if (!isNativeApp || appAddrApplied.current || !appAddr) return;
    if (appAddr.address_id) {
      const match = savedAddresses.find((a) => a.address_id === appAddr.address_id);
      if (match) {
        appAddrApplied.current = true;
        applyAddress(match);
      }
    } else if (appAddr.zone) {
      // Zone détectée par GPS (sans adresse sauvegardée) → pré-remplir la zone
      appAddrApplied.current = true;
      setForm((f) => ({ ...f, zone: f.zone || appAddr.zone }));
    }
  }, [savedAddresses, appAddr]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveCurrentAddress = async () => {
    const label = saveLabel.trim();
    if (!label || !form.address || !form.zone) return;
    setSavingAddr(true);
    try {
      const { data } = await apiClient.post("/me/addresses", {
        label, address: form.address, zone: form.zone,
        gps: gps ? { lat: gps.lat, lng: gps.lng, accuracy: gps.accuracy ?? null } : null,
      });
      setSavedAddresses((list) => {
        const others = list.filter((a) => a.address_id !== data.address_id);
        return [...others, data];
      });
      setSelectedAddrId(data.address_id);
      setSaveLabel("");
      toast.success(t("co.addressSaved", { label: data.label }));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingAddr(false);
    }
  };

  const shareLocation = () => {
    if (!("geolocation" in navigator)) {
      setGpsStatus("error");
      toast.error(t("co.errNoGeo"));
      return;
    }
    setGpsStatus("loading");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
        setGpsStatus("ok");
        toast.success(t("co.gpsShared"));
      },
      () => {
        setGpsStatus("error");
        toast.error(t("co.gpsFailed"));
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  };
  const [ppWait, setPpWait] = useState(null); // { order_id, paypal_order_id, simulated, amount_eur, ... }
  const [ppConfig, setPpConfig] = useState(null); // /payments/paypal/config
  useEffect(() => {
    // Taux GNF→EUR partagé PayPal / Carte : affiche le montant exact débité
    if ((form.payment_method === "paypal" || form.payment_method === "card") && !ppConfig) {
      apiClient.get("/payments/paypal/config").then(({ data }) => setPpConfig(data)).catch(() => {});
    }
  }, [form.payment_method]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    apiClient.get("/zones").then(({ data }) => setZones(data)).catch(() => {});
  }, []);

  // Vérifie que la position GPS est couverte par une zone de livraison active
  useEffect(() => {
    if (!gps?.lat) { setCoverage(null); return; }
    apiClient.post("/delivery-zones/check", { lat: gps.lat, lng: gps.lng })
      .then(({ data }) => setCoverage(data))
      .catch(() => setCoverage(null));
  }, [gps?.lat, gps?.lng]); // eslint-disable-line react-hooks/exhaustive-deps

  // Retour d'un paiement carte annulé (Stripe) : on annule la commande en
  // attente, le panier est conservé, retour au choix du moyen de paiement.
  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    if (qs.get("stripe") === "cancel") {
      const oid = qs.get("order_id");
      if (oid) {
        apiClient.post(`/orders/${oid}/cancel`, { reason: "autre", comment: "Paiement carte non confirmé (annulé par le client)" }).catch(() => {});
      }
      toast.error(t("co.cardCancelled"));
      window.history.replaceState({}, "", "/checkout");
    }
  }, []);

  // Load the address book (Maison, Bureau…) for 1-click selection
  useEffect(() => {
    if (!user || user.role !== "client") return;
    apiClient.get("/me/addresses").then(({ data }) => setSavedAddresses(data || [])).catch(() => {});
    apiClient.get("/me/recipients").then(({ data }) => setSavedRecipients(data || [])).catch(() => {});
  }, [user]);

  // Pre-fill the saved delivery address from a previous order (editable)
  useEffect(() => {
    if (!user) return;
    setForm((f) => ({
      ...f,
      address: f.address || user.default_address || "",
      zone: f.zone || user.default_zone || "",
      phone: f.phone || user.phone || "",
    }));
    // Restore the refined GPS point saved with the address — no need to re-share from home
    if (user.default_gps?.lat != null && user.default_gps?.lng != null) {
      setGps((g) => g || { lat: user.default_gps.lat, lng: user.default_gps.lng, accuracy: user.default_gps.accuracy ?? null, saved: true });
      setGpsStatus((s) => (s === "idle" ? "ok" : s));
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading && !user) {
      toast.info(t("co.loginFirst"));
      navigate("/login?next=/checkout");
    }
  }, [user, authLoading, navigate]);

  // Checkout is a client feature only — partners and admins don't order
  if (user && user.role !== "client") return <Navigate to={roleHome(user.role)} replace />;

  // Compte suspendu par l'admin : aucune nouvelle commande possible,
  // l'historique des commandes passées reste consultable dans le profil.
  if (user?.blocked) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="blocked-client-banner">
        <div className="w-16 h-16 mx-auto rounded-full bg-[#FDECEC] grid place-items-center mb-4">
          <ShieldCheck className="w-8 h-8 text-brand-red" />
        </div>
        <h1 className="font-display text-3xl font-semibold mb-2">{t("co.suspendedTitle")}</h1>
        <p className="text-mute mb-6">
          Vous ne pouvez plus passer de nouvelle commande pour le moment.
          Contactez le support NelDaari pour plus d'informations.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <a
            href={`https://wa.me/33758262447?text=${encodeURIComponent("Bonjour NelDaari, mon compte a été suspendu. Pouvez-vous m'aider ?")}`}
            target="_blank" rel="noopener noreferrer"
            data-testid="blocked-support-whatsapp"
            className="inline-flex items-center gap-2 bg-brand-green text-white px-5 py-3 rounded-full font-semibold"
          >
            <Phone className="w-4 h-4" /> Contacter le support (WhatsApp)
          </a>
          <Link to="/profile" data-testid="blocked-view-history"
            className="inline-flex items-center gap-2 border border-sand px-5 py-3 rounded-full font-semibold hover:border-ink">
            Voir mes commandes passées
          </Link>
        </div>
      </div>
    );
  }

  if (count === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <ShoppingBag className="w-12 h-12 mx-auto text-mute mb-4" />
        <h1 className="font-display text-3xl font-semibold">{t("co.emptyCart")}</h1>
        <Link to="/restaurants" className="inline-flex items-center gap-2 mt-4 bg-brand-red text-white px-5 py-3 rounded-full font-semibold">{t("co.explore")}</Link>
      </div>
    );
  }

  const deliveryFee = cart.restaurant?.delivery_fee || 0;
  const beforePoints = subtotal + deliveryFee;
  const userPoints = Math.max(0, parseInt(user?.loyalty_points || 0));
  const maxRedeem = Math.min(userPoints, Math.floor(beforePoints / 2));
  const effectiveUse = Math.min(usePoints, maxRedeem);
  const total = beforePoints - effectiveUse;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!isGift) {
      if (!form.address || !form.zone || !form.phone) {
        toast.error(t("co.errAddress"));
        return;
      }
      if (!isValidPhoneInternational(form.phone)) {
        toast.error(t("co.errPhone"));
        return;
      }
      if (!gps) {
        toast.error(t("co.errGps"));
        shareLocation();
        return;
      }
      if (coverage?.has_zones && !coverage.covered) {
        toast.error(t("co.errOutOfZone"));
        return;
      }
    }
    if (isGift) {
      if (!gift.recipient_name.trim()) {
        toast.error(t("co.errGiftName"));
        return;
      }
      if (!gift.recipient_phone || !isValidPhoneGuinea(gift.recipient_phone)) {
        toast.error(t("co.errGiftPhone"));
        return;
      }
      if (!gift.recipient_address.trim()) {
        toast.error(t("co.errGiftAddress"));
        return;
      }
      if (!gift.zone) {
        toast.error(t("co.errGiftZone"));
        return;
      }
    }
    const normalisedPhone = isGift
      ? (form.phone && isValidPhoneInternational(form.phone)
          ? "+" + normalisePhoneInternational(form.phone)
          : "+" + normalisePhoneGuinea(gift.recipient_phone))
      : "+" + normalisePhoneInternational(form.phone);
    if (form.payment_method === "orange_money" || form.payment_method === "mtn_money") {
      if (!form.momo_number || !isValidPhoneGuinea(form.momo_number)) {
        toast.error(t("co.errMomo"));
        return;
      }
    }
    // Carte bancaire : aucune saisie ici — la carte est saisie sur la page
    // sécurisée Stripe Checkout (redirection après création de la commande).
    setLoading(true);
    try {
      const payload = {
        restaurant_id: cart.restaurant.restaurant_id,
        items: cart.items.map((i) => {
          const sel = i.selected_options || [];
          return {
            dish_id: i.dish_id,
            quantity: i.quantity,
            removed_ingredients: sel.filter((o) => o.kind === "removed").map((o) => o.ingredient_id),
            extra_levels: sel.filter((o) => o.kind === "extra").map((o) => ({ ingredient_id: o.ingredient_id, level_index: o.level_index })),
            cooking: sel.find((o) => o.kind === "cooking")?.value || null,
            note: i.note || "",
          };
        }),
        address: isGift ? gift.recipient_address.trim() : form.address,
        zone: isGift ? gift.zone : form.zone,
        phone: normalisedPhone,
        payment_method: form.payment_method,
        notes: form.notes,
        use_points: effectiveUse,
        is_gift: isGift,
        gift_recipient_name: isGift ? gift.recipient_name.trim() : "",
        gift_recipient_phone: isGift ? "+" + normalisePhoneGuinea(gift.recipient_phone) : "",
        gift_message: isGift ? gift.message.trim() : "",
        gift_recipient_address: isGift ? gift.recipient_address.trim() : "",
        gift_recipient_landmark: isGift ? gift.recipient_landmark.trim() : "",
        payer_country: isGift ? gift.payer_country.trim() : "",
        client_lat: gps?.lat ?? null,
        client_lng: gps?.lng ?? null,
        client_gps_accuracy: gps?.accuracy ?? null,
      };
      let data;
      try {
        ({ data } = await apiClient.post("/orders", payload));
      } catch (err) {
        // Restaurant fermé avec horaires automatiques : confirmation explicite du différé
        const detail = err?.response?.status === 409 ? String(err.response.data?.detail || "") : "";
        if (detail.startsWith("SCHEDULED|")) {
          const when = fmtReopen(detail.split("|")[1], i18n.language);
          if (!window.confirm(t("co.scheduledAsk", { when }))) return;
          ({ data } = await apiClient.post("/orders", { ...payload, accept_scheduled: true }));
        } else {
          throw err;
        }
      }
      refreshNotifications(); // bell reflects "Commande confirmée" immediately

      // Card payment → Stripe Checkout (panier conservé jusqu'à confirmation)
      // App mobile : navigateur intégré Capacitor + polling (pas de deep link) ;
      // Web : redirection classique vers checkout.stripe.com
      if (form.payment_method === "card") {
        try {
          const stripeRes = await apiClient.post("/payments/stripe/checkout", {
            order_id: data.order_id,
            origin_url: isNativeApp ? process.env.REACT_APP_BACKEND_URL : window.location.origin,
            native: isNativeApp,
          });
          if (stripeRes.data?.url) {
            if (isNativeApp) {
              // Navigateur intégré Capacitor ; si le plugin n'est pas disponible
              // (build non synchronisé), repli sur le navigateur système.
              // Dans les deux cas, le retour est géré par polling (pas de deep link).
              let opened = false;
              try {
                const { Browser } = await import("@capacitor/browser");
                await Browser.open({ url: stripeRes.data.url });
                opened = true;
              } catch (e) { void e; }
              if (!opened) {
                try { window.open(stripeRes.data.url, "_blank"); opened = true; } catch (e) { void e; }
              }
              if (!opened) { window.location.href = stripeRes.data.url; return; }
              navigate(`/orders/${data.order_id}?stripe=pending`);
            } else {
              window.location.href = stripeRes.data.url;
            }
            return; // le panier n'est vidé qu'après confirmation du paiement (OrderTracking)
          }
        } catch (payErr) {
          // Stripe indisponible → annuler la commande fraîchement créée, retour au choix
          try { await apiClient.post(`/orders/${data.order_id}/cancel`, { reason: "autre", comment: "Paiement carte indisponible" }); } catch (e2) { void e2; }
          toast.error(t("co.cardUnavailable"));
          setLoading(false);
          return;
        }
      }

      // MTN MoMo → request-to-pay
      if (form.payment_method === "mtn_money") {
        try {
          const momoRes = await apiClient.post("/payments/momo/checkout", {
            order_id: data.order_id,
            msisdn: "+" + normalisePhoneGuinea(form.momo_number || form.phone),
          });
          if (momoRes.data?.reference_id) {
            toast.success(t("co.momoSent"));
          }
        } catch (payErr) {
          toast.warning(t("co.momoFailed"));
        }
      }

      // PayPal (diaspora) → la commande ne part qu'après capture confirmée
      if (form.payment_method === "paypal") {
        try {
          const ppRes = await apiClient.post("/payments/paypal/create", { order_id: data.order_id });
          setPpWait({ order_id: data.order_id, ...ppRes.data });
          setLoading(false);
          return; // cart kept; navigation happens only after capture SUCCESS
        } catch (payErr) {
          try { await apiClient.post(`/orders/${data.order_id}/cancel`, { reason: "autre", comment: "Paiement PayPal indisponible" }); } catch (e2) { void e2; }
          toast.error(t("co.paypalUnavailable"));
          setLoading(false);
          return;
        }
      }

      // Orange Money → Web Payment : la commande ne part qu'après confirmation
      if (form.payment_method === "orange_money") {
        try {
          const omRes = await apiClient.post("/payments/om/checkout", { order_id: data.order_id });
          if (omRes.data?.payment_url) window.open(omRes.data.payment_url, "_blank", "noopener");
          setOmWait({ order_id: data.order_id, pay_token: omRes.data?.pay_token, simulated: !!omRes.data?.simulated });
          setLoading(false);
          return; // cart kept; navigation happens only after payment SUCCESS
        } catch (payErr) {
          // OM unavailable → cancel the freshly-created order, back to payment choice
          try { await apiClient.post(`/orders/${data.order_id}/cancel`, { reason: "autre", comment: "Paiement Orange Money indisponible" }); } catch {}
          toast.error(t("co.omUnavailable"));
          setLoading(false);
          return;
        }
      }

      clear();
      toast.success(t("co.orderSuccess"));
      navigate(`/orders/${data.order_id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const abandonOmPayment = async (silent = false) => {
    const oid = omWait?.order_id;
    setOmWait(null);
    if (oid) {
      try { await apiClient.post(`/orders/${oid}/cancel`, { reason: "autre", comment: "Paiement Orange Money non confirmé" }); } catch {}
    }
    if (!silent) toast.error(t("co.omNotConfirmed"));
  };

  const onOmPaid = (oid) => {
    setOmWait(null);
    clear();
    toast.success(t("co.omConfirmed"));
    navigate(`/orders/${oid}`);
  };

  const abandonPaypal = async (silent = false) => {
    const oid = ppWait?.order_id;
    setPpWait(null);
    if (oid) {
      try { await apiClient.post(`/orders/${oid}/cancel`, { reason: "autre", comment: "Paiement PayPal non confirmé" }); } catch (e) { void e; }
    }
    if (!silent) toast.error(t("co.paypalNotConfirmed"));
  };

  const onPaypalPaid = (oid, reference) => {
    setPpWait(null);
    clear();
    toast.success(`Paiement PayPal confirmé (réf. ${reference}) — commande passée !`);
    navigate(`/orders/${oid}`);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {omWait && <OmWaitModal wait={omWait} total={total} onPaid={onOmPaid} onFail={abandonOmPayment} />}
      {ppWait && <PaypalModal wait={ppWait} config={ppConfig} onPaid={onPaypalPaid} onFail={abandonPaypal} />}
      <h1 className="font-display text-3xl sm:text-4xl font-semibold mb-2">{t("checkout.title")}</h1>
      <p className="text-mute mb-8">{t("checkout.deliveryInfo")}</p>

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {!isGift && (
          <section className="bg-white border border-sand rounded-2xl p-6">
            <h2 className="font-display text-xl font-semibold mb-4 flex items-center gap-2"><MapPin className="w-5 h-5 text-brand-red" /> {t("checkout.deliveryInfo")}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {savedAddresses.length > 0 && (
                <div className="sm:col-span-2" data-testid="address-book-chips">
                  <label className="text-xs uppercase tracking-widest text-mute mb-1 block">{t("co.myAddresses")}</label>
                  <div className="flex flex-wrap gap-2">
                    {savedAddresses.map((a) => (
                      <button
                        type="button"
                        key={a.address_id}
                        onClick={() => applyAddress(a)}
                        data-testid={`address-chip-${a.address_id}`}
                        className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${selectedAddrId === a.address_id ? "bg-brand-red text-white border-brand-red" : "bg-elevated text-ink border-sand hover:border-brand-red"}`}
                      >
                        <MapPin className="w-3.5 h-3.5" /> {a.label}
                        {a.gps?.lat != null && <span className={`text-[9px] font-bold rounded-full px-1.5 py-0.5 ${selectedAddrId === a.address_id ? "bg-white/25" : "bg-brand-green/15 text-brand-green"}`}>GPS</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-widest text-mute mb-1 block">{t("checkout.address")} *</label>
                <input data-testid="checkout-address" value={form.address} onChange={(e) => { set("address", e.target.value); setSelectedAddrId(null); }} placeholder={t("checkout.addressPlaceholder")} className="w-full bg-elevated rounded-xl px-4 py-3 outline-none" required />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-mute mb-1 block">{t("checkout.zone")} *</label>
                <select data-testid="checkout-zone" value={form.zone} onChange={(e) => set("zone", e.target.value)} className="w-full bg-elevated rounded-xl px-4 py-3 outline-none" required>
                  <option value="">{t("checkout.zone")}</option>
                  {zones.map((z) => <option key={z} value={z}>{z}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-mute mb-1 block">{t("checkout.phone")} *</label>
                <div className="flex items-center gap-2 bg-elevated rounded-xl px-3">
                  <Phone className="w-4 h-4 text-mute" />
                  <input data-testid="checkout-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder={t("checkout.phonePlaceholder")} className="w-full bg-transparent py-3 outline-none" required />
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-widest text-mute mb-1 block">Position GPS {isGift ? "(facultatif pour un cadeau)" : "*"}</label>
                <div className={`rounded-xl border px-4 py-3 flex items-center justify-between gap-3 flex-wrap ${gpsStatus === "ok" ? "bg-[#F0F9F4] border-[#B7DFC7]" : "bg-elevated border-sand"}`} data-testid="checkout-gps-block">
                  <div className="flex items-center gap-2 text-sm min-w-0">
                    {gpsStatus === "ok" ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 text-brand-green shrink-0" />
                        <span className="text-brand-green font-semibold" data-testid="checkout-gps-ok">{gps.saved ? t("co.gpsSaved") : t("co.gpsSharedShort")}</span>
                        <span className="text-mute text-xs truncate">({gps.lat.toFixed(5)}, {gps.lng.toFixed(5)})</span>
                      </>
                    ) : (
                      <>
                        <LocateFixed className="w-4 h-4 text-mute shrink-0" />
                        <span className="text-mute">{t("co.gpsPrompt")}</span>
                      </>
                    )}
                  </div>
                  <button type="button" onClick={shareLocation} disabled={gpsStatus === "loading"} data-testid="checkout-share-gps"
                    className={`shrink-0 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${gpsStatus === "ok" ? "border border-brand-green text-brand-green hover:bg-brand-green/10" : "bg-brand-red text-white hover:bg-[#C81F2D]"} disabled:opacity-60`}>
                    {gpsStatus === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <LocateFixed className="w-4 h-4" />}
                    {gpsStatus === "ok" ? t("co.gpsRefresh") : t("co.gpsShareBtn")}
                  </button>
                </div>
                {gpsStatus === "ok" && gps && (
                  <ClientPositionMap
                    lat={gps.lat}
                    lng={gps.lng}
                    onChange={(lat, lng) => setGps((g) => ({ ...g, lat, lng, accuracy: null, manual: true }))}
                  />
                )}
                {gpsStatus === "ok" && (
                  <div className="text-[11px] text-mute mt-1">{t("co.gpsCheckMarker")}</div>
                )}
                {gpsStatus === "ok" && coverage?.has_zones && !coverage.covered && (
                  <div data-testid="checkout-out-of-zone" className="mt-2 rounded-xl border border-brand-red/40 bg-brand-red/10 px-4 py-3 text-sm text-brand-red font-medium">
                    {t("co.outOfZoneBanner")}
                  </div>
                )}
                {gpsStatus === "ok" && coverage?.has_zones && coverage.covered && coverage.zone && (
                  <div data-testid="checkout-zone-covered" className="mt-2 text-xs text-brand-green font-semibold">
                    {t("co.zoneCovered", { zone: coverage.zone })}
                  </div>
                )}
                <div className="flex items-center gap-2 mt-3" data-testid="save-address-row">
                  <input
                    value={saveLabel}
                    onChange={(e) => setSaveLabel(e.target.value)}
                    maxLength={40}
                    placeholder={t("co.saveAsPlaceholder")}
                    className="flex-1 min-w-0 bg-elevated rounded-xl px-4 py-2.5 text-sm outline-none"
                    data-testid="save-address-label"
                  />
                  <button
                    type="button"
                    onClick={saveCurrentAddress}
                    disabled={savingAddr || !saveLabel.trim() || !form.address || !form.zone}
                    data-testid="save-address-btn"
                    className="shrink-0 rounded-full border border-sand hover:border-brand-red text-sm font-semibold px-4 py-2.5 disabled:opacity-50 inline-flex items-center gap-1.5"
                  >
                    {savingAddr ? <Loader2 className="w-4 h-4 animate-spin" /> : "💾"} {t("common.save")}
                  </button>
                </div>
              </div>

              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-widest text-mute mb-1 block">{t("checkout.notes")}</label>
                <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder={t("checkout.notesPlaceholder")} className="w-full bg-elevated rounded-xl px-4 py-3 outline-none" rows={2} />
              </div>
            </div>
          </section>
          )}

          <section className={`rounded-2xl p-6 border-2 transition-colors ${isGift ? "bg-[#FFFBEA] border-brand-yellow" : "bg-[#FFFDF3] border-brand-yellow/70"}`} data-testid="gift-section">
            {fromDiaspora && isGift && (
              <div className="mb-4 flex items-center gap-2 rounded-xl bg-brand-yellow text-ink px-4 py-2.5 text-sm font-bold" data-testid="gift-diaspora-banner">
                🎁 Mode cadeau activé — vous commandez depuis l'étranger pour un proche. Remplissez simplement ses coordonnées ci-dessous.
              </div>
            )}
            <button
              type="button"
              onClick={() => setIsGift(!isGift)}
              data-testid="gift-toggle"
              className="w-full flex items-center justify-between gap-3"
            >
              <span className="flex items-center gap-3 text-left">
                <span className="w-11 h-11 rounded-2xl bg-brand-yellow/40 grid place-items-center text-xl shrink-0">🎁</span>
                <span>
                  <span className="font-display text-xl font-semibold block">{t("co.giftTitle")}</span>
                  <span className="text-xs text-mute block mt-0.5">
                    {t("co.giftDesc")}
                  </span>
                </span>
              </span>
              <span className={`relative inline-flex h-7 w-13 min-w-[52px] shrink-0 rounded-full transition-colors ${isGift ? "bg-brand-green" : "bg-sand"}`}>
                <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-transform ${isGift ? "translate-x-[26px]" : "translate-x-1"}`} />
              </span>
            </button>
            {isGift && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4" data-testid="gift-fields">
                {savedRecipients.length > 0 && (
                  <div className="sm:col-span-2" data-testid="recipient-book-chips">
                    <label className="text-xs uppercase tracking-widest text-mute mb-1 block">{t("co.myRecipients")}</label>
                    <div className="flex flex-wrap gap-2">
                      {savedRecipients.map((r) => (
                        <button
                          type="button"
                          key={r.recipient_id}
                          onClick={() => applyRecipient(r)}
                          data-testid={`recipient-chip-${r.recipient_id}`}
                          className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${selectedRecipientId === r.recipient_id ? "bg-brand-red text-white border-brand-red" : "bg-white text-ink border-sand hover:border-brand-red"}`}
                        >
                          🎁 {r.name}
                          <span className={`text-[10px] font-normal ${selectedRecipientId === r.recipient_id ? "opacity-80" : "text-mute"}`}>{r.zone}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <label className="text-xs uppercase tracking-widest text-mute mb-1 block">{t("co.giftName")} *</label>
                  <input
                    data-testid="gift-recipient-name"
                    value={gift.recipient_name}
                    onChange={(e) => setGift((g) => ({ ...g, recipient_name: e.target.value }))}
                    placeholder={t("co.phGiftName")}
                    className="w-full bg-elevated rounded-xl px-4 py-3 outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-mute mb-1 block">{t("co.giftPhone")} *</label>
                  <div className="flex items-center gap-2 bg-elevated rounded-xl px-3">
                    <Phone className="w-4 h-4 text-mute" />
                    <input
                      data-testid="gift-recipient-phone"
                      value={gift.recipient_phone}
                      onChange={(e) => setGift((g) => ({ ...g, recipient_phone: e.target.value }))}
                      placeholder="+224 6XX XX XX XX"
                      className="w-full bg-transparent py-3 outline-none"
                    />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs uppercase tracking-widest text-mute mb-1 block">{t("co.giftMessage")}</label>
                  <textarea
                    data-testid="gift-message"
                    value={gift.message}
                    onChange={(e) => setGift((g) => ({ ...g, message: e.target.value }))}
                    placeholder={t("co.phGiftMessage")}
                    className="w-full bg-elevated rounded-xl px-4 py-3 outline-none"
                    rows={2}
                    maxLength={280}
                  />
                  <div className="text-[11px] text-mute text-right">{gift.message.length}/280</div>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs uppercase tracking-widest text-mute mb-1 block">{t("co.giftAddress")} *</label>
                  <input
                    data-testid="gift-recipient-address"
                    value={gift.recipient_address}
                    onChange={(e) => setGift((g) => ({ ...g, recipient_address: e.target.value }))}
                    placeholder={t("co.phGiftAddress")}
                    className="w-full bg-elevated rounded-xl px-4 py-3 outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-mute mb-1 block">{t("co.giftZone")} *</label>
                  <select
                    data-testid="gift-recipient-zone"
                    value={gift.zone}
                    onChange={(e) => setGift((g) => ({ ...g, zone: e.target.value }))}
                    className="w-full bg-elevated rounded-xl px-4 py-3 outline-none"
                  >
                    <option value="">{t("co.chooseZone")}</option>
                    {zones.map((z) => <option key={z} value={z}>{z}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-mute mb-1 block">{t("co.landmark")}</label>
                  <input
                    data-testid="gift-recipient-landmark"
                    value={gift.recipient_landmark}
                    onChange={(e) => setGift((g) => ({ ...g, recipient_landmark: e.target.value }))}
                    placeholder={t("co.phLandmark")}
                    className="w-full bg-elevated rounded-xl px-4 py-3 outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-mute mb-1 block">{t("co.payerCountry")}</label>
                  <input
                    data-testid="gift-payer-country"
                    value={gift.payer_country}
                    onChange={(e) => setGift((g) => ({ ...g, payer_country: e.target.value }))}
                    placeholder={t("co.phPayerCountry")}
                    className="w-full bg-elevated rounded-xl px-4 py-3 outline-none"
                  />
                </div>
                <div className="sm:col-span-2 flex items-center justify-between gap-3 flex-wrap rounded-xl border border-dashed border-sand px-3 py-2.5" data-testid="save-recipient-row">
                  <span className="text-xs text-mute">{t("co.saveRecipient")}</span>
                  <button
                    type="button"
                    onClick={saveCurrentRecipient}
                    disabled={savingRecipient}
                    data-testid="save-recipient-btn"
                    className="shrink-0 rounded-full border border-sand hover:border-brand-red text-sm font-semibold px-4 py-2 disabled:opacity-50 inline-flex items-center gap-1.5"
                  >
                    {savingRecipient ? <Loader2 className="w-4 h-4 animate-spin" /> : "💾"} {t("co.saveRecipientBtn")}
                  </button>
                </div>
                <div className="sm:col-span-2 rounded-xl bg-elevated px-3 py-2 text-xs text-mute">
                  🔐 Le code de livraison à 4 chiffres sera communiqué <b>au destinataire</b> via un lien de suivi partageable (WhatsApp) — pas à vous.
                </div>
              </div>
            )}
          </section>

          <CheckoutSuggestions
            restaurant={cart.restaurant}
            cartItems={cart.items}
            onAdd={(dish) => addItem(cart.restaurant, dish)}
          />

          <section className="bg-white border border-sand rounded-2xl p-6">
            <h2 className="font-display text-xl font-semibold mb-4 flex items-center gap-2"><Banknote className="w-5 h-5 text-brand-red" /> {t("checkout.paymentMethod")}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {PAYMENTS.map((p) => {
                const Icon = p.icon;
                const active = form.payment_method === p.key;
                return (
                  <button type="button" key={p.key} onClick={() => set("payment_method", p.key)} data-testid={`payment-${p.key}`}
                    className={`text-left rounded-xl p-4 border-2 transition-all ${active ? "border-brand-red bg-[#FFF5F6]" : "border-sand bg-white hover:border-mute"}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full grid place-items-center" style={{ backgroundColor: p.color + "22", color: p.color }}><Icon className="w-4 h-4" /></div>
                      <span className="font-semibold">{t(p.labelKey)}</span>
                    </div>
                  </button>
                );
              })}
            </div>

            {(form.payment_method === "orange_money" || form.payment_method === "mtn_money") && (
              <div className="mt-4">
                <label className="text-xs uppercase tracking-widest text-mute mb-1 block">{t("co.momoNumber")}</label>
                <input data-testid="momo-number" value={form.momo_number} onChange={(e) => set("momo_number", e.target.value)} placeholder="+224 6XX XX XX XX" className="w-full bg-elevated rounded-xl px-4 py-3 outline-none" />
                <p className="text-xs text-mute mt-2">{t("co.momoHint")}</p>

                {/* Admin-only test-mode panel (MTN MoMo sandbox) */}
                {user?.role === "admin" && form.payment_method === "mtn_money" && (
                  <div className="mt-3 rounded-xl bg-[#FFF8E5] border border-dashed border-[#F4C430] p-3" data-testid="momo-test-panel">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#946A00] font-semibold mb-2">
                      <FlaskConical className="w-3.5 h-3.5" /> Mode test MoMo (sandbox)
                    </div>
                    <p className="text-xs text-mute mb-2">Numéros sandbox MTN pour tester le flux e2e sans utiliser un vrai numéro.</p>
                    <div className="flex flex-wrap gap-2">
                      {MOMO_TEST_MSISDN.map((item) => {
                        const Icon = item.icon;
                        const active = form.momo_number === item.msisdn;
                        return (
                          <button
                            key={item.key}
                            type="button"
                            onClick={() => set("momo_number", item.msisdn)}
                            data-testid={`momo-test-${item.key}`}
                            className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 border transition-colors ${active ? "bg-ink text-white border-ink" : "bg-white text-ink border-sand hover:border-[#F4C430]"}`}
                          >
                            <Icon className="w-3 h-3" style={{ color: active ? item.color : item.color }} />
                            {item.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
            {form.payment_method === "card" && (
              <div className="mt-4 rounded-xl bg-[#F0F9F4] border border-[#B7DFC7] p-4 flex items-start gap-3" data-testid="stripe-secure-badge">
                <ShieldCheck className="w-5 h-5 text-brand-green shrink-0 mt-0.5" />
                <div className="text-sm w-full">
                  <div className="font-semibold text-ink">{t("checkout.secureBadge")}</div>
                  <p className="text-mute text-xs mt-1">
                    Vos informations de carte ne transitent jamais par nos serveurs. Vous serez redirigé vers la page sécurisée Stripe pour valider votre paiement.
                  </p>
                  {ppConfig?.eur_gnf_rate > 0 && (
                    <div className="mt-2 rounded-lg bg-white border border-sand px-3 py-2 text-xs" data-testid="card-fx-amount">
                      <span className="font-semibold text-ink">
                        Montant débité : {(total / ppConfig.eur_gnf_rate).toFixed(2)} EUR
                      </span>
                      <span className="text-mute"> (taux : 1 EUR = {Math.round(ppConfig.eur_gnf_rate).toLocaleString("fr-FR")} GNF)</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {form.payment_method === "paypal" && (
              <div className="mt-4 rounded-xl bg-[#EEF3FB] border border-[#B8CCEC] p-4" data-testid="paypal-fx-block">
                <div className="flex items-start gap-3">
                  <Globe2 className="w-5 h-5 text-[#003087] shrink-0 mt-0.5" />
                  <div className="text-sm w-full">
                    <div className="font-semibold text-ink flex items-center gap-2 flex-wrap">
                      Paiement international via PayPal
                      {ppConfig && !ppConfig.configured && (
                        <span className="rounded-full bg-brand-yellow/30 text-[#8A6D00] px-2 py-0.5 text-[10px] font-bold" data-testid="paypal-sim-badge">MODE SIMULÉ</span>
                      )}
                    </div>
                    <p className="text-mute text-xs mt-1">
                      Idéal depuis l'étranger : PayPal ou carte bancaire internationale. Les prix restent en GNF — seul le débit se fait en devise.
                    </p>
                    {ppConfig && (
                      <div className="mt-2 rounded-lg bg-white border border-sand px-3 py-2 text-xs" data-testid="paypal-fx-amount">
                        {ppConfig.currency === "USD" && ppConfig.usd_gnf_rate ? (
                          <>
                            <div className="font-bold text-ink">
                              Montant débité : {(total / ppConfig.usd_gnf_rate).toFixed(2)} USD
                              <span className="font-normal text-mute"> (taux : 1 USD = {Math.round(ppConfig.usd_gnf_rate).toLocaleString("fr-FR")} GNF)</span>
                            </div>
                            <div className="text-mute mt-0.5">
                              ≈ {(total / ppConfig.eur_gnf_rate).toFixed(2)} EUR (taux : 1 EUR = {Math.round(ppConfig.eur_gnf_rate).toLocaleString("fr-FR")} GNF)
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="font-bold text-ink">
                              Montant débité : {(total / ppConfig.eur_gnf_rate).toFixed(2)} EUR
                              <span className="font-normal text-mute"> (taux : 1 EUR = {Math.round(ppConfig.eur_gnf_rate).toLocaleString("fr-FR")} GNF)</span>
                            </div>
                            {ppConfig.usd_gnf_rate && (
                              <div className="text-mute mt-0.5">
                                ≈ {(total / ppConfig.usd_gnf_rate).toFixed(2)} USD (taux : 1 USD = {Math.round(ppConfig.usd_gnf_rate).toLocaleString("fr-FR")} GNF)
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>

        <aside className="bg-white border border-sand rounded-2xl p-6 h-fit sticky top-24">
          <h3 className="font-display text-xl font-semibold mb-4">{t("cart.total")}</h3>
          <div className="space-y-2 text-sm mb-3">
            {cart.items.map((i) => (
              <div key={i.line_id} className="flex justify-between gap-2" data-testid={`co-summary-line-${i.line_id}`}>
                <span className="min-w-0">
                  {i.quantity}× {i.name}
                  {i.selected_options?.length > 0 && (
                    <span className="block text-xs text-mute" data-testid={`co-summary-options-${i.line_id}`}>
                      {i.selected_options.map((o) => o.price_delta > 0 ? `${o.name} (+${formatGNF(o.price_delta)})` : o.name).join(" · ")}
                    </span>
                  )}
                  {i.note && <span className="block text-xs text-mute italic">« {i.note} »</span>}
                </span>
                <span className="font-medium whitespace-nowrap">{formatGNF((i.price + (i.options_total || 0)) * i.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-sand pt-3 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-mute">{t("cart.subtotal")}</span><span>{formatGNF(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-mute">{t("cart.delivery")}</span><span>{formatGNF(deliveryFee)}</span></div>
            {effectiveUse > 0 && (
              <div className="flex justify-between text-brand-green">
                <span>{t("checkout.loyaltyBlockTitle")}</span><span>-{formatGNF(effectiveUse)}</span>
              </div>
            )}
            <div className="flex justify-between font-display text-lg font-semibold pt-2 border-t border-sand mt-2">
              <span>{t("cart.total")}</span><span>{formatGNF(total)}</span>
            </div>
          </div>

          {userPoints > 0 && maxRedeem > 0 && (
            <div className="mt-4 bg-elevated rounded-xl p-3" data-testid="loyalty-redeem-block">
              <div className="text-xs uppercase tracking-widest text-brand-green mb-2 flex items-center gap-1.5">
                <Gift className="w-3.5 h-3.5" /> {t("checkout.loyaltyBlockTitle")}
              </div>
              <div className="text-sm mb-2">{t("checkout.loyaltyBalance")} : <strong>{userPoints}</strong> · {t("checkout.loyaltyMax")} : <strong>{maxRedeem}</strong></div>
              <input
                type="range" min="0" max={maxRedeem} step="100" value={usePoints}
                onChange={(e) => setUsePoints(parseInt(e.target.value))}
                data-testid="loyalty-slider"
                className="w-full accent-[#009E60]"
              />
              <div className="text-xs text-mute flex justify-between mt-1">
                <span>0</span>
                <span className="font-semibold text-ink">{t("checkout.loyaltyUse", { n: effectiveUse })} (−{formatGNF(effectiveUse)})</span>
                <span>{maxRedeem}</span>
              </div>
            </div>
          )}
          <button type="submit" disabled={loading} data-testid="place-order-btn" className="w-full mt-5 rounded-full bg-brand-red hover:bg-[#C81F2D] disabled:opacity-60 text-white py-3 font-semibold">
            {loading ? t("checkout.placing") : `${t("checkout.confirmOrder")} · ${formatGNF(total)}`}
          </button>
          <p className="text-xs text-mute mt-2 text-center">{t((PAYMENTS.find((p) => p.key === form.payment_method) || {}).labelKey || "checkout.cash")}</p>
        </aside>
      </form>
    </div>
  );
}

/** Orange Money payment waiting modal — polls status every 4 s, 2 min timeout.
 * Failure/timeout → order auto-cancelled, user back to payment choice (cart kept). */
function OmWaitModal({ wait, total, onPaid, onFail }) {
  const { t } = useTranslation();
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    let stopped = false;
    const t0 = Date.now();
    const tick = setInterval(() => setElapsed(Math.floor((Date.now() - t0) / 1000)), 1000);
    const poll = setInterval(async () => {
      try {
        const { data } = await apiClient.get(`/payments/om/status/${wait.order_id}`);
        if (stopped) return;
        if (data.payment_status === "paid") { stopped = true; onPaid(wait.order_id); }
        else if (data.payment_status === "failed") { stopped = true; onFail(); }
      } catch { /* transient — keep polling */ }
    }, 4000);
    return () => { stopped = true; clearInterval(tick); clearInterval(poll); };
  }, [wait.order_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const remaining = Math.max(0, 120 - elapsed);
  return (
    <div className="fixed inset-0 z-[70] bg-ink/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="om-wait-modal">
      <div className="w-full max-w-sm bg-white rounded-3xl p-6 text-center shadow-2xl">
        <div className="w-14 h-14 rounded-2xl mx-auto mb-4 grid place-items-center" style={{ backgroundColor: "#FF790022" }}>
          <Smartphone className="w-7 h-7" style={{ color: "#FF7900" }} />
        </div>
        <h3 className="font-display text-xl font-bold mb-1">{t("co.omModalTitle")}</h3>
        <p className="text-sm text-mute mb-3">
          {wait.simulated
            ? t("co.omSimulated")
            : t("co.omValidate")}
        </p>
        <div className="rounded-xl bg-elevated px-3 py-2 text-xs font-mono mb-3" data-testid="om-ref">
          Réf : {wait.pay_token} · {formatGNF(total)}
        </div>
        {wait.simulated && (
          <div className="rounded-xl bg-brand-yellow/20 text-[#8A6D00] px-3 py-2 text-[11px] font-bold mb-3" data-testid="om-simulated-badge">
            PAIEMENT SIMULÉ — aucun débit réel
          </div>
        )}
        <div className="flex items-center justify-center gap-2 text-sm text-mute mb-4">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60" style={{ backgroundColor: "#FF7900" }} />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ backgroundColor: "#FF7900" }} />
          </span>
          En attente de confirmation… <b className="tabular-nums">{Math.floor(remaining / 60)}:{String(remaining % 60).padStart(2, "0")}</b>
        </div>
        <button onClick={() => onFail()} data-testid="om-cancel-payment" className="w-full rounded-xl border border-sand hover:border-brand-red text-mute hover:text-brand-red py-3 text-sm font-semibold">
          Annuler et choisir un autre moyen
        </button>
      </div>
    </div>
  );
}

// ================= PayPal payment modal (diaspora) =================
function PaypalModal({ wait, config, onPaid, onFail }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const chargeCur = wait.charge_currency || "EUR";
  const chargeAmt = Number(wait.charge_amount ?? wait.amount_eur);
  const chargeRate = chargeCur === "USD" ? wait.usd_gnf_rate : wait.eur_gnf_rate;

  const capture = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post("/payments/paypal/capture", { order_id: wait.order_id });
      if (data.paid) onPaid(wait.order_id, data.reference);
      else onFail();
    } catch (err) {
      toast.error(formatApiError(err));
      onFail();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 grid place-items-center p-4" data-testid="paypal-modal">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md">
        <div className="flex items-center gap-2 mb-1">
          <Globe2 className="w-5 h-5 text-[#003087]" />
          <h3 className="font-display text-xl font-semibold">{t("co.paypalModalTitle")}</h3>
          {wait.simulated && (
            <span className="rounded-full bg-brand-yellow/30 text-[#8A6D00] px-2 py-0.5 text-[10px] font-bold" data-testid="paypal-modal-sim">SIMULÉ</span>
          )}
        </div>
        <div className="rounded-xl bg-[#EEF3FB] border border-[#B8CCEC] px-4 py-3 my-3 text-sm" data-testid="paypal-modal-fx">
          <div className="font-bold">{t("co.amountCharged", { amt: chargeAmt.toFixed(2), cur: chargeCur })}</div>
          <div className="text-xs text-mute mt-0.5">
            {t("co.fxLine", { cur: chargeCur, rate: Math.round(chargeRate).toLocaleString("fr-FR"), total: formatGNF(wait.amount_gnf) })}
          </div>
          {chargeCur === "USD" ? (
            <div className="text-xs text-mute">≈ {Number(wait.amount_eur).toFixed(2)} EUR</div>
          ) : (
            wait.amount_usd && <div className="text-xs text-mute">≈ {Number(wait.amount_usd).toFixed(2)} USD</div>
          )}
        </div>

        {wait.simulated ? (
          <>
            <p className="text-xs text-mute mb-3">
              Identifiants PayPal non configurés — paiement simulé pour la démonstration.
              Référence : <b className="font-mono">{wait.paypal_order_id}</b>
            </p>
            <button
              onClick={capture}
              disabled={busy}
              data-testid="paypal-sim-pay"
              className="w-full rounded-full bg-[#003087] hover:bg-[#00256B] disabled:opacity-60 text-white px-6 py-3 font-semibold inline-flex items-center justify-center gap-2"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Payer {chargeAmt.toFixed(2)} {chargeCur} (SIMULÉ)
            </button>
          </>
        ) : (
          <PayPalScriptProvider options={{
            clientId: config?.client_id,
            currency: config?.currency || "EUR",
            intent: "capture",
            locale: "fr_FR",
            // buyer-country is a SANDBOX-ONLY param: forces the buyer jurisdiction
            // to match the FR merchant/buyer accounts (avoids buyerCountry=US mismatch)
            ...(config?.mode === "sandbox" ? { "buyer-country": "FR" } : {}),
          }}>
            <PayPalButtons
              style={{ layout: "vertical", label: "pay" }}
              createOrder={() => Promise.resolve(wait.paypal_order_id)}
              onApprove={capture}
              onCancel={() => onFail()}
              onError={() => onFail()}
            />
          </PayPalScriptProvider>
        )}

        <button onClick={() => onFail()} data-testid="paypal-abandon" className="w-full mt-3 text-sm text-mute underline">
          Annuler et revenir au choix du paiement
        </button>
      </div>
    </div>
  );
}
